import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  Clock,
  History,
  Pause,
  CalendarX,
  ExternalLink,
  RefreshCw,
} from "lucide-react";

const REASON_META = {
  no_activity_14_days: { label: "Idle 14+ days", Icon: Pause, tone: "text-amber-700 bg-amber-50 ring-amber-200" },
  close_date_changed_2plus: { label: "Pushed 2+ times", Icon: History, tone: "text-red-700 bg-red-50 ring-red-200" },
  stuck_in_stage: { label: "Stuck in stage", Icon: Clock, tone: "text-amber-700 bg-amber-50 ring-amber-200" },
  closing_soon_no_activity_7: { label: "Closing soon · silent 7d", Icon: ShieldAlert, tone: "text-red-700 bg-red-50 ring-red-200" },
  past_close_date: { label: "Past close date", Icon: CalendarX, tone: "text-red-700 bg-red-50 ring-red-200" },
};

function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

export default function RiskRadarPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/metrics/latest");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await api.post("/metrics/recompute");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRecomputing(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="risk-loading">
        Loading risk radar…
      </div>
    );
  }

  if (data.empty) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="risk-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <ShieldAlert className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No risk data yet</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            Once metrics are computed after your first sync + stage mapping, at-risk deals will
            appear here.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => nav("/dashboard/connect")}
              data-testid="risk-connect-cta"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              Set up now
            </button>
            <button
              onClick={recompute}
              disabled={recomputing}
              className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${recomputing ? "animate-spin" : ""}`} />
              Recompute
            </button>
          </div>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const cur = m.currency || "USD";
  const deals = m.at_risk_deals || [];
  const totalAmount = deals.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="risk-page">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Risk Radar</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Deals needing attention
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            Open deals with at least one risk signal, sorted by amount. Snapshot {m.metric_date}.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 flex gap-6" data-testid="risk-summary">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">At risk</div>
            <div className="mt-1 text-2xl font-extrabold text-slate-900 number-tabular" data-testid="risk-count">
              {deals.length}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Amount at risk</div>
            <div className="mt-1 text-2xl font-extrabold text-slate-900 number-tabular" data-testid="risk-total">
              {fmtMoney(totalAmount, cur)}
            </div>
          </div>
        </div>
      </header>

      {deals.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 p-12 text-center flex flex-col items-center"
          data-testid="risk-clear"
        >
          <ShieldAlert className="h-10 w-10 text-emerald-500" />
          <h2 className="mt-4 text-xl font-semibold text-emerald-900">All clear</h2>
          <p className="mt-2 text-sm text-emerald-800/80 max-w-sm">
            No open deals hit any risk signal at this snapshot. Keep it up.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {deals.map((d) => (
            <div
              key={d.deal_id}
              className="flex flex-col md:flex-row md:items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors"
              data-testid={`risk-row-${d.deal_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {d.deal_name || "Untitled deal"}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5 rounded">
                    {d.standard_stage}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {d.reasons.map((r) => {
                    const meta = REASON_META[r] || { label: r, Icon: ShieldAlert, tone: "text-slate-700 bg-slate-100 ring-slate-200" };
                    const Icon = meta.Icon;
                    return (
                      <span
                        key={r}
                        className={`inline-flex items-center gap-1 rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}
                        data-testid={`risk-reason-${d.deal_id}-${r}`}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    );
                  })}
                </div>
                <div className="text-xs text-slate-500">
                  Suggested: <span className="text-slate-700 font-medium">{d.suggested_action}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 md:gap-6 md:min-w-[240px] md:justify-end">
                <div className="text-right">
                  <div className="text-lg font-extrabold text-slate-900 number-tabular">
                    {fmtMoney(d.amount, d.currency || cur)}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {d.close_date ? `closes ${new Date(d.close_date).toLocaleDateString()}` : "no close date"}
                  </div>
                </div>
                {d.hubspot_url && (
                  <a
                    href={d.hubspot_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`risk-hubspot-link-${d.deal_id}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Open in HubSpot <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {err && <div className="text-sm text-red-600" data-testid="risk-error">{err}</div>}
    </div>
  );
}
